/**
 * Seed catalogue.
 *
 * Used as the offline fallback and as the initial content for a fresh
 * Firestore project. Once workshops exist in Firestore they become the source
 * of truth and this file is only a safety net — the site still renders if the
 * database is unreachable, which matters when you are paying for the traffic
 * that is landing on it.
 *
 * ⚠ These are still placeholder entries. Replace the trainers, dates, prices
 *   and WhatsApp links with your real workshops before running ads — then
 *   publish them with “Seed workshops to Firestore” in the admin Settings tab.
 */

export const INITIAL_WORKSHOPS = [
  {
    id: 'ws-101',
    title: 'AWS Cloud & DevOps Career Launchpad',
    category: 'Technology',
    instructor: 'Arjun Nair',
    instructorTitle: 'Cloud Solutions Architect, ex-Amazon',
    date: '2026-08-19',
    time: '07:00 PM IST',
    duration: '4 days · Live',
    originalPrice: 2499,
    price: 699,
    seatsLeft: 9,
    maxSeats: 60,
    rating: 4.9,
    reviewsCount: 287,
    badge: 'BEST SELLER',
    banner: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1000&q=80',
    whatsappLink: 'https://chat.whatsapp.com/EduspireCloudDevOps',
    description:
      'Go from zero to deploying a live application on AWS. Hands-on with EC2, S3, IAM and a real CI/CD pipeline, guided by a working solutions architect.',
    outcome: 'Deploy a live app on AWS',
    highlights: [
      'Hands-on EC2, S3 & IAM setup',
      'CI/CD pipeline with GitHub Actions',
      'AWS certification roadmap',
      'Lifetime WhatsApp community access',
    ],
    curriculum: [
      {
        id: 'day-1',
        label: 'Day 1',
        title: 'Cloud Foundations',
        subtitle: 'Understand what the cloud actually is before you touch a console.',
        lessons: [
          { title: 'How the Cloud Really Works', body: 'Regions, availability zones and why any of it matters to your application.' },
          { title: 'Your First EC2 Instance', body: 'Launch a server, connect to it, and understand what you are paying for.' },
          { title: 'Storage With S3', body: 'Buckets, objects and static hosting — the backbone of most AWS work.' },
          { title: 'IAM Without the Fear', body: 'Users, roles and policies explained in plain language, with least privilege from day one.' },
        ],
      },
      {
        id: 'day-2',
        label: 'Day 2',
        title: 'Ship Something Real',
        subtitle: 'A running server is not a deployment. Today you deploy properly.',
        lessons: [
          { title: 'Networking Essentials', body: 'VPCs, subnets and security groups — enough to be dangerous, not enough to be lost.' },
          { title: 'Deploy Your Application', body: 'Take a real app from your laptop to a public URL.' },
          { title: 'Domains, HTTPS & Load Balancing', body: 'Point a real domain at it and terminate TLS the way production does.' },
        ],
      },
      {
        id: 'day-3',
        label: 'Day 3',
        title: 'Automate Everything',
        subtitle: 'If you deploy by hand, you will eventually deploy a mistake.',
        lessons: [
          { title: 'Git Workflow for Teams', body: 'Branches, pull requests and the review habits real teams run on.' },
          { title: 'Build a CI/CD Pipeline', body: 'GitHub Actions that test and deploy on every push.' },
          { title: 'Infrastructure as Code', body: 'Describe your stack in a file so it can be rebuilt from scratch.' },
        ],
      },
      {
        id: 'day-4',
        label: 'Day 4',
        title: 'Run It Like a Professional',
        subtitle: 'Getting it live is half the job. Keeping it live is the other half.',
        lessons: [
          { title: 'Monitoring & Alerts', body: 'CloudWatch metrics and alarms so you hear about problems before your users do.' },
          { title: 'Cost Control', body: 'Where AWS bills quietly grow, and how to cap them.' },
          { title: 'Certification Roadmap', body: 'Which AWS certification to target next and how to prepare for it.' },
          { title: 'Your Portfolio Project', body: 'Package what you built into something you can show an interviewer.' },
        ],
      },
    ],
  },
  {
    id: 'ws-102',
    title: 'Data Analytics with Python, SQL & Power BI',
    category: 'Technology',
    instructor: 'Kavya Iyer',
    instructorTitle: 'Senior Data Analyst, ex-Flipkart',
    date: '2026-08-23',
    time: '06:30 PM IST',
    duration: '3 days · Live',
    originalPrice: 1999,
    price: 599,
    seatsLeft: 14,
    maxSeats: 50,
    rating: 4.88,
    reviewsCount: 341,
    badge: 'TRENDING',
    banner: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1000&q=80',
    whatsappLink: 'https://chat.whatsapp.com/EduspireDataAnalytics',
    description:
      'Turn raw data into decisions. Clean and query real datasets with Python and SQL, then build an interactive Power BI dashboard you can put in your portfolio.',
    outcome: 'Build a portfolio dashboard',
    highlights: [
      'Real dataset case studies',
      'SQL for working analysts',
      'Interactive Power BI dashboards',
      'Verified Eduspire certificate',
    ],
  },
  {
    id: 'ws-103',
    title: 'Medical Coding & Clinical Documentation Certification',
    category: 'Healthcare',
    instructor: 'Dr. Neha Kulkarni',
    instructorTitle: 'MBBS · Certified Professional Coder (CPC)',
    date: '2026-08-26',
    time: '07:30 PM IST',
    duration: '2 days · Live',
    originalPrice: 2999,
    price: 899,
    seatsLeft: 11,
    maxSeats: 40,
    rating: 4.85,
    reviewsCount: 198,
    badge: 'POPULAR',
    banner: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1000&q=80',
    whatsappLink: 'https://chat.whatsapp.com/EduspireMedicalCoding',
    description:
      'A practical entry into the medical coding industry. Learn ICD-10 and CPT coding on real clinical charts, taught by a doctor who codes for a living.',
    outcome: 'Job-ready in medical coding',
    highlights: [
      'ICD-10 & CPT fundamentals',
      'Practice on real clinical charts',
      'Industry-recognised certificate',
      'Placement guidance',
    ],
  },
  {
    id: 'ws-104',
    title: 'Meta & Instagram Ads Growth Masterclass',
    category: 'Marketing',
    instructor: 'Faizan Ahmed',
    instructorTitle: 'Performance Marketer, 8 yrs',
    date: '2026-08-29',
    time: '05:00 PM IST',
    duration: '2 days · Live',
    originalPrice: 1799,
    price: 499,
    seatsLeft: 6,
    maxSeats: 60,
    rating: 4.92,
    reviewsCount: 412,
    badge: 'MUST ATTEND',
    banner: 'https://images.unsplash.com/photo-1611926653458-09294b3142bf?auto=format&fit=crop&w=1000&q=80',
    whatsappLink: 'https://chat.whatsapp.com/EduspireMetaAds',
    description:
      'Build, launch and read a real ad campaign during the session. Audience targeting, retargeting and scroll-stopping creative — built around a live ₹5,000 budget.',
    outcome: 'Run your first profitable ad',
    highlights: [
      'Live campaign build (₹5k budget)',
      'Audience & retargeting setup',
      'Creative that actually converts',
      'Exclusive mentor WhatsApp group',
    ],
  },
  {
    id: 'ws-105',
    title: 'Spoken English & Confident Communication',
    category: 'Career',
    instructor: 'Raja Mandal',
    instructorTitle: 'Corporate Communication Coach',
    date: '2026-09-02',
    time: '06:00 PM IST',
    duration: '5 days · Live',
    originalPrice: 1499,
    price: 399,
    seatsLeft: 20,
    maxSeats: 80,
    rating: 4.87,
    reviewsCount: 526,
    badge: 'NEW BATCH',
    banner: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=1000&q=80',
    whatsappLink: 'https://chat.whatsapp.com/EduspireSpokenEnglish',
    description:
      'Stop hesitating and start speaking. Daily live speaking practice, grammar made simple, and group conversation drills that build real confidence.',
    outcome: 'Speak English without hesitation',
    highlights: [
      'Daily live speaking practice',
      'Grammar made simple',
      'Group conversation drills',
      'Practice buddy WhatsApp group',
    ],
    curriculum: [
      {
        id: 'day-1',
        label: 'Day 1',
        title: 'Break the Fear',
        subtitle: 'Your first step is not English. It is CONFIDENCE.',
        lessons: [
          {
            title: 'Identify Your Fear',
            body: 'Why do you hesitate, freeze or avoid speaking?',
          },
          {
            title: 'Change Your Priority',
            body: 'Stop chasing perfect English. Start focusing on effective communication.',
          },
          {
            title: 'Build the Right Mindset',
            body: 'Mistakes are not failures. Mistakes are part of fluency.',
          },
          {
            title: 'Understand WHY & HOW',
            body: 'Know why you want to improve — and discover how to improve effectively.',
          },
          {
            title: 'Discover the 3 Keys to Better Communication',
            body: 'Learn the three fundamental areas that can transform your communication.',
          },
          {
            title: 'Break the Past-Mistake Cycle',
            body: 'Understand what you did wrong before — and learn what to do differently now.',
          },
          {
            title: 'Take Action',
            body: 'Your first speaking challenge: speak without fear.',
          },
        ],
      },
      {
        id: 'day-2',
        label: 'Day 2',
        title: 'Start Speaking With Correct Sentences',
        subtitle: 'Practice makes progress. Perfect comes with practice.',
        lessons: [
          {
            title: 'Understand How Your Brain Learns',
            body: 'Discover the neuroscience of learning and how regular practice helps your brain build stronger language patterns.',
          },
          {
            title: 'Learn Smart Grammar',
            body: 'No boring grammar. No endless rules. Learn only the grammar you actually need to speak correctly and confidently.',
          },
          {
            title: 'Create the Magic of Sentence Structure',
            body: 'Stop searching for words and struggling to form sentences. Learn a simple system to think, structure and speak faster.',
          },
          {
            title: 'Build Your Speaking Blocks',
            body: 'Learn how to create sentences quickly using simple, practical structures instead of memorising complicated grammar.',
          },
        ],
      },
      {
        id: 'day-3',
        label: 'Day 3',
        title: 'Speak With Flow',
        subtitle: 'Fluency is not about speaking fast. It is about speaking without getting stuck.',
        lessons: [
          {
            title: 'Think From the Right Brain & Speak',
            body: 'Stop translating in your mind. Train yourself to think directly in English, form ideas and speak naturally and effortlessly.',
          },
          {
            title: 'Build Vocabulary Power From the Heart',
            body: "Don't memorise difficult words. Learn to use simple, natural and powerful words that help you express from the heart and connect with people.",
          },
          {
            title: 'Connect Creativity in Speaking',
            body: 'Add imagination, emotion and expression to your speech. Speak in a way that feels natural, engaging and alive.',
          },
          {
            title: 'Speak Without Getting Stuck',
            body: 'Learn simple techniques to manage pauses, forgotten words and hesitation smoothly and confidently.',
          },
          {
            title: 'The Art of Continuous Speaking',
            body: "Don't stop when you make mistakes. Just follow: pause, think, continue speaking.",
          },
          {
            title: 'Real-Life Conversation Practice',
            body: 'Move beyond textbook English. Practice through pair activities, real-life situations and spontaneous speaking exercises.',
          },
        ],
      },
      {
        id: 'day-4',
        label: 'Day 4',
        title: 'Discover Yourself',
        subtitle: 'Speak English like never before.',
        lessons: [
          {
            title: 'Right Brain Speaking Practice',
            body: 'Speak before you translate — think directly in English.',
            points: [
              'Direct English thought flow',
              'Inner voice activation',
              'Natural idea emergence',
            ],
          },
          {
            title: 'Express Through Body Language',
            body: 'Non-verbal communication: let your body speak before words.',
            points: [
              'Hand gesture awareness',
              'Facial expression control',
              'Posture confidence building',
            ],
          },
          {
            title: 'Public Speaking Like Never Before',
            body: 'Speak to be heard, not just to talk.',
            points: [
              'Stage presence development',
              'Audience connection skill',
              'Fearless speaking exposure',
            ],
          },
          {
            title: 'Speak With Confidence and Power-Packed Energy',
            body: 'Own every word you speak.',
            points: [
              'Strong voice projection',
              'Clear and bold expression',
              'Eliminate the hesitation mindset',
            ],
          },
          {
            title: 'Continuous Speaking Mastery',
            body: 'Extempore delivery: speak continuously with natural rhythm.',
            points: [
              'Uninterrupted speaking flow',
              'Instant idea generation',
              'No-pause fear training',
            ],
          },
          {
            title: 'Group Discussion on Real-Life Topics',
            body: 'Learn to agree, disagree and express freely.',
            points: [
              'Interactive speaking practice',
              'Opinion sharing confidence',
              'Real-world communication skills',
            ],
          },
          {
            title: 'Magical Way to Build Vocabulary',
            body: 'A practical system for growing the words you actually use.',
          },
        ],
      },
      {
        id: 'day-5',
        label: 'Day 5',
        title: 'New Start With the End',
        subtitle: 'Last-day transformation — a new beginning through final clarity.',
        lessons: [
          {
            title: 'Role Play Activities',
            body: 'Real-life communication mastery: break hesitation through live interaction.',
            points: [
              'Situational speaking practice',
              'Real-world conversation simulation',
              'Confidence building through action',
              'Learn to respond, react and express naturally',
            ],
          },
          {
            title: 'Final Road Map of Success',
            body: 'Your personal blueprint — turning learning into a lifelong skill.',
            points: [
              'Clear vision of your communication journey',
              'Step-by-step growth strategy',
              'Daily speaking habits for mastery',
              'Long-term confidence development plan',
            ],
          },
          {
            title: 'How to Change Your Belief System',
            body: 'Mindset reprogramming: shift identity from learner to speaker.',
            points: [
              'Identify limiting beliefs in communication',
              'Replace fear with confidence thinking',
              'Build empowering self-talk patterns',
              'Train your mind for fearless expression',
            ],
          },
          {
            title: 'Acknowledgment and Announcement',
            body: 'Declare your new speaking identity.',
            points: [
              'Recognising your transformation journey',
              'Celebrating progress and effort',
              'Sharing achievements with confidence',
              'Public acknowledgment of growth',
            ],
          },
          {
            title: 'Feedback Session',
            body: 'Reflection and improvement: turn feedback into future growth.',
            points: [
              'Understanding personal strengths',
              'Identifying areas of improvement',
              'Constructive feedback exchange',
              'Learning from experience and practice',
            ],
          },
          {
            title: 'Decision Making',
            body: 'The final commitment: become a confident communicator by choice.',
            points: [
              'Choosing consistency over comfort',
              'Committing to daily speaking practice',
              'Taking responsibility for growth',
              'Deciding to continue the journey of mastery',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'ws-106',
    title: 'Resume, LinkedIn & Interview Mastery',
    category: 'Career',
    instructor: 'Sameer Joshi',
    instructorTitle: 'HR Head · hiring since 2012',
    date: '2026-09-06',
    time: '04:00 PM IST',
    duration: '1 day · Intensive',
    originalPrice: 1299,
    price: 349,
    seatsLeft: 17,
    maxSeats: 50,
    rating: 4.9,
    reviewsCount: 233,
    badge: 'HIRING SEASON',
    banner: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1000&q=80',
    whatsappLink: 'https://chat.whatsapp.com/EduspireInterviewPrep',
    description:
      'See yourself the way a recruiter does. Build an ATS-friendly resume, rework your LinkedIn, and run a recorded mock interview with feedback from a hiring head.',
    outcome: 'Interview-ready in a day',
    highlights: [
      'ATS-friendly resume rebuild',
      'LinkedIn profile makeover',
      'Mock interview with feedback',
      'Direct HR networking',
    ],
  },
];

export const CATEGORIES = ['All', 'Technology', 'Healthcare', 'Marketing', 'Career'];

export const DEFAULT_ADMIN_SETTINGS = {
  /** Which workshop the landing page sells. Empty = the first in the catalogue.
   *  The page is a single funnel and shows exactly one workshop, so this is how
   *  the admin swaps which batch is being advertised. */
  featuredWorkshopId: '',
  supportWhatsapp: import.meta.env.VITE_SUPPORT_WHATSAPP || '+91 87772 89980',
  supportEmail: import.meta.env.VITE_SUPPORT_EMAIL || 'eduspire.2019@gmail.com',
  companyAddress:
    'Eduspire Career Development Pvt. Ltd., Aurora Waterfront Building, 4th Floor, Unit No: 405, Salt Lake, Sector V, Kolkata - 700 091',
  gstin: '19AAFCE2604Q1ZB',
  cin: 'U80903WB2018PTC228808',
  legalName: 'Eduspire Career Development Private Limited',
};
